import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import UnauthorizedAccess from "components/RequirePermission/UnauthorizedAccess";
import RefreshButton from "components/shared/RefreshButton/RefreshButton";
import { PRESETS } from "pages/admin/components/CopilotDashboard/datePresets";
import { useDashboardSettings } from "pages/admin/components/CopilotDashboard/useDashboardSettings";
import { useCopilotExportJob } from "pages/admin/components/CopilotDashboard/useCopilotExportJob";
import { useGetActiveOrganizationsQuery, useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import {
  useGetCostCenterListQuery,
  useGetMeteredUsageCostCentersQuery,
  useGetMeteredUsageMoversQuery,
  useGetMeteredUsageOrganizationsQuery,
  useGetMeteredUsageProductsQuery,
  useGetMeteredUsageQualityQuery,
  useGetMeteredUsageSummaryQuery,
  useGetMeteredUsageTrendQuery,
  useGetMeteredUsageUsersQuery,
  useStartMeteredUsageExportJobMutation,
} from "state/rtkQuery/user/userQueries";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";
import { sortByLabel } from "utils/sortByLabel";
import { useDebounce } from "utils/useDebounce";

import FullListDialog from "./FullListDialog";
import ProductSkuFilter from "./ProductSkuFilter";
import RangeCalendar from "./RangeCalendar";
import { QUALITY_MAX_DAYS, availableMetrics, deloitteLabelPercent, hasDeloitteFields, productColorMap, formatMoney, formatShare, metricLabel, rangeDays, rangeError, resolveRange } from "./meteredFormat";
import { productLabel, skuLabel } from "./meteredLabels";
import QualityPanel from "./MeteredQuality";
import { useMeteredUsageRefresh } from "./useMeteredUsageRefresh";
import { ACCENT, Card, DELOITTE_GREEN, DeloitteBodyCells, DeloitteHeadCells, EmptyNote, ExpandButton, HeaderDatePill, KpiCards, ProductBars, ProductShareDonut, ProductsTable, TrendChart, TrendLines, bodyCellSx, headCellSx } from "./MeteredParts";

const TOP_ROWS = 10; // shown in each card; the expand button opens the full, paged list
const PER_PAGE = 10;
// Cards side by side share this header height so their green rules line up.
const HEADER_MIN_HEIGHT = 96;
// The trend is always stacked by product, so a spike shows which product drove it. (The API can also group by SKU.)
const TREND_GROUP_BY = "product";
// The Data Coverage panel (and its /quality request) is built but switched off for now. Turn this on to bring both back.
const DATA_COVERAGE_ENABLED = false;
// The What Changed (movers) section and its /movers request are switched off for now. Turn this on (and uncomment the
// container below) to bring both back. To restore it fully: set this to true, add back
//   import WhatChanged from "./MeteredChanges";
// and uncomment moverTab, canOpenMover / openMover and the <WhatChanged> container.
const MOVERS_ENABLED = false;
// The server caps a metered export's date range, tighter for the enterprise-wide one.
const EXPORT_MAX_DAYS = { org: 90, enterprise: 31 };

// Starting an export can fail in ways worth naming (it is switched off until configured on the server).
const exportStartMessage = (err) => {
  const detail = typeof err?.data?.detail === "string" ? err.data.detail : null;
  if (err?.status === 503) return "Metered usage export isn't enabled on the server yet.";
  if (err?.status === 403) return "You don't have permission to start this export.";
  return detail ?? "The export couldn't be started. Please try again later.";
};
const ACTION_GREEN = "#26890d";
const ACTION_GREEN_HOVER = "#1f6e0a";
const ACTION_BLUE = "#006eff"; // the Copilot dashboard's first-load spinner

const pillSx = (active) => ({
  borderRadius: "16px",
  textTransform: "none",
  fontSize: "0.8rem",
  fontWeight: active ? 600 : 400,
  backgroundColor: active ? ACTION_GREEN : "transparent",
  color: active ? "#fff" : "#555",
  border: `1px solid ${active ? ACTION_GREEN : "#ccc"}`,
  px: 1.5,
  py: 0.3,
  minWidth: 0,
  "&:hover": { backgroundColor: active ? ACTION_GREEN_HOVER : "#f0f0f0", border: `1px solid ${active ? ACTION_GREEN_HOVER : "#bbb"}` },
  // A disabled pill (e.g. "By SKU" before a product is picked) keeps the pill shape instead of MUI's square grey default.
  "&.Mui-disabled": { borderRadius: "16px", textTransform: "none", backgroundColor: "transparent", border: "1px solid #e0e0e0", color: "#bbb" },
});

// The orgs / cost centers a row was billed to. A person billed to several has a null scalar and a list, so the list is
// shown rather than calling them unassigned; only a row with no entries at all is "Unassigned" / "Unmapped".
const billedNames = (row, listKey, nameKey, emptyLabel) => {
  const fromList = (row[listKey] ?? []).map((item) => item?.[nameKey] ?? emptyLabel);
  const names = fromList.length ? fromList : [row[nameKey] ?? emptyLabel];
  return [...new Set(names)];
};

const NameChips = ({ names, testId }) => (
  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }} data-testid={testId}>
    {names.slice(0, 3).map((name) => (
      <Chip key={name} size="small" label={name} sx={{ fontSize: "0.7rem", height: 20 }} />
    ))}
    {names.length > 3 && <Chip size="small" label={`+${names.length - 3}`} title={names.slice(3).join(", ")} sx={{ fontSize: "0.7rem", height: 20 }} />}
  </Box>
);

const totalCellSx = { ...bodyCellSx, fontWeight: 700, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa" };

// One row per person, with the names the API resolves. The "Unattributed" row is usage that has no username — shown
// distinctly and never clickable; a username with no matching person is "Unmapped" and not clickable either.
// Shares and the totals row use the response's totals (every matching row, before paging), for the chosen measure.
const UsersTable = ({ rows, startIndex, enterprise, metric, totals, totalCount, canOpen, onOpen, deloitteRate, discountPercent }) => {
  const whole = totals?.[metric] ?? 0;
  const showDeloitte = deloitteRate !== undefined || hasDeloitteFields(totals);
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headCellSx, width: 48 }}>#</TableCell>
            <TableCell sx={headCellSx}>Person</TableCell>
            <TableCell sx={headCellSx}>Cost center</TableCell>
            {enterprise && <TableCell sx={headCellSx}>Organization</TableCell>}
            <TableCell sx={headCellSx} align="right">Gross</TableCell>
            <TableCell sx={headCellSx} align="right">Discount</TableCell>
            <TableCell sx={headCellSx} align="right">Net</TableCell>
            {showDeloitte && <DeloitteHeadCells percent={discountPercent} />}
            <TableCell sx={headCellSx} align="right">Share of {metricLabel(metric).toLowerCase()}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            const clickable = canOpen && !row.is_unattributed && row.user_id;
            const unmapped = !row.is_unattributed && !row.user_id && row.user_name;
            const value = row[metric] ?? 0;
            return (
              <TableRow
                key={row.is_unattributed ? "unattributed" : row.user_id ?? `${row.user_name}-${idx}`}
                onClick={clickable ? () => onOpen(row) : undefined}
                sx={{ "&:hover": clickable ? { backgroundColor: "#f9fdf2", cursor: "pointer" } : {}, backgroundColor: row.is_unattributed ? "#fff8ec" : "inherit", "&:last-child td": { border: 0 } }}
                data-testid={row.is_unattributed ? "unattributed-row" : "user-row"}
              >
                <TableCell sx={{ ...bodyCellSx, color: "#999" }}>{row.is_unattributed ? "—" : startIndex + idx + 1}</TableCell>
                <TableCell sx={bodyCellSx}>
                  {row.is_unattributed ? (
                    <>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Unattributed</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#8a5200" }}>usage with no username — not a person</Typography>
                    </>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.3 }}>{row.full_name ?? row.user_name ?? "Unknown user"}</Typography>
                        {unmapped && <Chip size="small" label="Unmapped user" title="A username with no matching person, so there's no detail page" sx={{ fontSize: "0.66rem", height: 18, backgroundColor: "#ffe7bf", color: "#8a5200" }} />}
                      </Box>
                      <Typography sx={{ fontSize: "0.72rem", color: "#999" }}>{row.email ?? (row.user_name ? `@${row.user_name}` : "—")}</Typography>
                    </>
                  )}
                </TableCell>
                <TableCell sx={bodyCellSx}>{row.is_unattributed ? "—" : <NameChips names={billedNames(row, "cost_centers", "cost_center_name", "Unassigned")} testId="user-cost-centers" />}</TableCell>
                {enterprise && <TableCell sx={bodyCellSx}>{row.is_unattributed ? "—" : <NameChips names={billedNames(row, "organizations", "org_name", "Unmapped")} testId="user-orgs" />}</TableCell>}
                <TableCell sx={bodyCellSx} align="right">{formatMoney(row.gross_amount)}</TableCell>
                <TableCell sx={bodyCellSx} align="right">{formatMoney(row.discount_amount)}</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACCENT }} align="right">{formatMoney(row.net_amount)}</TableCell>
                {showDeloitte && <DeloitteBodyCells amount={row} rate={deloitteRate} sx={bodyCellSx} />}
                <TableCell sx={{ ...bodyCellSx, minWidth: 110 }} align="right">
                  <Typography sx={{ fontSize: "0.8rem" }}>{formatShare(value, whole)}</Typography>
                  <Box sx={{ height: 4, borderRadius: 4, backgroundColor: "#eee", mt: 0.4, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${whole > 0 ? Math.min(Math.max((value / whole) * 100, 1), 100) : 0}%`, backgroundColor: DELOITTE_GREEN }} />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {totals && (
            <TableRow data-testid="users-total">
              <TableCell sx={totalCellSx} />
              <TableCell sx={totalCellSx}>Total{typeof totalCount === "number" ? ` · ${totalCount.toLocaleString("en-US")} ${totalCount === 1 ? "row" : "rows"}` : ""}</TableCell>
              <TableCell sx={totalCellSx} />
              {enterprise && <TableCell sx={totalCellSx} />}
              <TableCell sx={totalCellSx} align="right">{formatMoney(totals.gross_amount)}</TableCell>
              <TableCell sx={totalCellSx} align="right">{formatMoney(totals.discount_amount)}</TableCell>
              <TableCell sx={{ ...totalCellSx, color: ACCENT }} align="right">{formatMoney(totals.net_amount)}</TableCell>
              {showDeloitte && <DeloitteBodyCells amount={totals} rate={deloitteRate} sx={totalCellSx} />}
              <TableCell sx={totalCellSx} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Cost centers (and organizations): a name, gross / discount / net, and a share of the whole. The share's whole is the
// response's total across every group — never the subtotal of the rows on screen. Server-resolved names include
// "Unassigned" / "Unmapped" for the catch-all buckets, which aren't links.
const GroupTable = ({ rows, nameKey, idKey, nameHeader, metric, totals, onOpen, testId, deloitteRate, discountPercent }) => {
  const whole = totals?.[metric] ?? 0;
  const showDeloitte = deloitteRate !== undefined || hasDeloitteFields(totals);
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>{nameHeader}</TableCell>
            <TableCell sx={headCellSx} align="right">Gross</TableCell>
            <TableCell sx={headCellSx} align="right">Discount</TableCell>
            <TableCell sx={headCellSx} align="right">Net</TableCell>
            {showDeloitte && <DeloitteHeadCells percent={discountPercent} />}
            <TableCell sx={{ ...headCellSx, width: 170 }}>Share of {metricLabel(metric).toLowerCase()}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const clickable = Boolean(onOpen && row[idKey]);
            const value = row[metric] ?? 0;
            return (
              <TableRow
                key={row[idKey] ?? row[nameKey]}
                onClick={clickable ? () => onOpen(row) : undefined}
                sx={{ "&:hover": clickable ? { backgroundColor: "#f9fdf2", cursor: "pointer" } : {}, "&:last-child td": { border: 0 } }}
                data-testid={testId}
              >
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row[nameKey] ?? (row[idKey] ? "Unknown" : "Unassigned")}</TableCell>
                <TableCell sx={bodyCellSx} align="right">{formatMoney(row.gross_amount)}</TableCell>
                <TableCell sx={bodyCellSx} align="right">{formatMoney(row.discount_amount)}</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACCENT }} align="right">{formatMoney(row.net_amount)}</TableCell>
                {showDeloitte && <DeloitteBodyCells amount={row} rate={deloitteRate} sx={bodyCellSx} />}
                <TableCell sx={bodyCellSx}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>{formatShare(value, whole)}</Typography>
                  <Box sx={{ height: 6, borderRadius: 4, backgroundColor: "#eee", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${whole > 0 ? Math.min(Math.max((value / whole) * 100, 1), 100) : 0}%`, backgroundColor: DELOITTE_GREEN }} />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {totals && (
            <TableRow data-testid="group-total">
              <TableCell sx={totalCellSx}>Total · all {nameHeader.toLowerCase()}s</TableCell>
              <TableCell sx={totalCellSx} align="right">{formatMoney(totals.gross_amount)}</TableCell>
              <TableCell sx={totalCellSx} align="right">{formatMoney(totals.discount_amount)}</TableCell>
              <TableCell sx={{ ...totalCellSx, color: ACCENT }} align="right">{formatMoney(totals.net_amount)}</TableCell>
              {showDeloitte && <DeloitteBodyCells amount={totals} rate={deloitteRate} sx={totalCellSx} />}
              <TableCell sx={totalCellSx} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// A failed panel says so and offers a retry — it never falls back to showing $0.
const LoadError = ({ children, onRetry }) => (
  <EmptyNote>
    {children}{" "}
    <Button size="small" onClick={onRetry} sx={{ textTransform: "none", color: ACCENT, fontStyle: "normal" }}>
      Retry
    </Button>
  </EmptyNote>
);

// Billing usage across every GitHub product, for one organization (scope "org") or the whole enterprise
// (scope "enterprise" — a separate API tier with its own permissions, plus an Organizations breakdown).
// Laid out like the Copilot dashboard. Everything you pick lives in the URL, so a link restores the same view.
//
// On the enterprise page an organization can be picked (?org=<id>). The enterprise API has no organization filter, so
// the page then asks the organization-level endpoints for that organization — the same way the enterprise Copilot
// dashboard does — using `orgCaps` (the person's permissions for that organization) instead of `caps`.
const NO_CAPS = {};
const MeteredUsageDashboard = ({ scope: pageScope, caps: pageCaps, orgCaps }) => {
  const { orgId: routeOrgId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageIsEnterprise = pageScope === "enterprise";
  const orgFilter = pageIsEnterprise ? searchParams.get("org") ?? "" : "";
  // What the data is about right now: the whole enterprise, or one organization (the page's own, or the one picked).
  const scope = pageIsEnterprise && orgFilter ? "org" : pageScope;
  const enterprise = scope === "enterprise";
  const orgId = scope === "org" ? (pageIsEnterprise ? orgFilter : routeOrgId) : undefined;
  const caps = pageIsEnterprise && orgFilter ? orgCaps ?? NO_CAPS : pageCaps;
  const { platformId } = useAccessPlatformId();
  // The organization picker's list (enterprise page only), A–Z.
  const { data: activeOrgs } = useGetActiveOrganizationsQuery(undefined, { skip: !pageIsEnterprise });
  const orgChoices = useMemo(() => sortByLabel(activeOrgs, "name"), [activeOrgs]);
  const { data: orgData } = useGetOrgByIdQuery(orgId, { skip: !orgId });

  // Deloitte's negotiated overage rate (the central Copilot dashboard setting). Used only while the page is looking at
  // the AI Credits SKU: its tables and totals then also show the overage discount and what Deloitte pays.
  const { deloitteOverageRate } = useDashboardSettings();

  // ── What is picked (all in the URL) ──
  const range = resolveRange(searchParams);
  const product = searchParams.get("product") ?? "";
  const sku = searchParams.get("sku") ?? "";
  const costCenterId = searchParams.get("cc") ?? "";
  const granularity = searchParams.get("gran") === "month" ? "month" : "day";
  // The line chart at the bottom: its own choice of lines (by product by default), cumulative or not, and day or month.
  // It always covers the date range picked at the top, and follows the page's measure, product, SKU and cost center.
  const linesParam = searchParams.get("lines");
  const lineGroup = linesParam === "total" ? "" : linesParam === "sku" && product ? "sku" : "product";
  const cumulative = searchParams.get("cum") === "1";
  const lineGranularity = searchParams.get("lgran") === "month" ? "month" : "day";
  // One Deloitte discount on everything: the rate comes from the central Copilot dashboard setting.
  const deloitteRate = deloitteOverageRate;
  const moverGroups = enterprise ? ["user", "product", "cost_center", "organization"] : ["user", "product", "cost_center"];
  const moverGroup = moverGroups.includes(searchParams.get("mvg")) ? searchParams.get("mvg") : "product";
  // const [moverTab, setMoverTab] = useState("inc"); // What Changed is off for now (see MOVERS_ENABLED)
  const [productView, setProductView] = useState("bars"); // "bars" | "donut"
  const [qualityOpen, setQualityOpen] = useState(false);

  const update = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace: true });
  };
  // Custom opens one calendar to pick the first and last day, then Apply. A preset applies straight away.
  const [activePreset, setActivePreset] = useState(range.preset);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  useEffect(() => {
    // The applied range changed (a preset, a link, back/forward): the highlighted pill follows it.
    setActivePreset(range.preset);
  }, [range.preset, range.from, range.to]);
  const choosePreset = (key, event) => {
    if (key === "custom") {
      setActivePreset("custom");
      setCalendarAnchor(event?.currentTarget ?? null);
      return;
    }
    setActivePreset(key);
    setCalendarAnchor(null);
    update({ preset: key === "this_month" ? null : key, from: null, to: null });
  };
  const closeCalendar = () => {
    setCalendarAnchor(null);
    setActivePreset(range.preset); // closing without Apply leaves the applied range (and its pill) as it was
  };
  const applyCustomRange = (from, to) => {
    setCalendarAnchor(null);
    update({ preset: "custom", from, to });
  };

  // ── Data ──
  const base = { scope, platformId, orgId, fromDate: range.from, toDate: range.to };
  // A reversed range is rejected here, before any request goes out.
  const rangeProblem = rangeError(range);
  const ready = Boolean(platformId) && (enterprise || Boolean(orgId)) && !rangeProblem;
  const filterArgs = { ...base, costCenterId, product, sku };

  // The summary always asks for the previous-period comparison: the same length of time immediately before the range.
  const summary = useGetMeteredUsageSummaryQuery({ ...filterArgs, compare: "previous_period" }, { skip: !ready || !caps.canViewSummary });
  // The Deloitte figures come from the API on every amount; an older API has none, so the Deloitte measures are not offered.
  const apiHasDeloitte = hasDeloitteFields(summary.data?.total);
  const metrics = availableMetrics(apiHasDeloitte);
  const metric = metrics.some((m) => m.key === searchParams.get("metric")) ? searchParams.get("metric") : "net_amount";
  // The discount as a percent for labels: the API's own, else worked out from the central rate.
  const discountPercent = deloitteLabelPercent(summary.data?.deloitte_discount?.percent, deloitteRate);
  const trend = useGetMeteredUsageTrendQuery({ ...filterArgs, granularity, groupBy: TREND_GROUP_BY }, { skip: !ready || !caps.canViewTrend });
  const products = useGetMeteredUsageProductsQuery({ ...base, costCenterId }, { skip: !ready || !caps.canViewProducts });
  // The card always shows page 1 sorted by net cost, 10 rows. The expand window pages / searches / sorts the rest.
  const topUsers = useGetMeteredUsageUsersQuery(
    { ...filterArgs, sortBy: "gross_amount", sortOrder: "desc", page: 1, perPage: TOP_ROWS },
    { skip: !ready || !caps.canViewUsers }
  );
  const costCenters = useGetMeteredUsageCostCentersQuery({ ...base, product, sku, sortBy: "net_amount", sortOrder: "desc" }, { skip: !ready || !caps.canViewCostCenters });
  const organizations = useGetMeteredUsageOrganizationsQuery(
    { scope: "enterprise", platformId, fromDate: range.from, toDate: range.to, product, sku, sortBy: "net_amount", sortOrder: "desc" },
    { skip: !ready || !enterprise || !caps.canViewOrganizations }
  );

  // Same arguments as the trend above when both are grouped by product by day, so that case is one shared request.
  const lineTrend = useGetMeteredUsageTrendQuery(
    { ...filterArgs, granularity: lineGranularity, groupBy: lineGroup || undefined },
    { skip: !ready || !caps.canViewTrend }
  );

  // What changed since the previous period, for the chosen grouping and measure (no user filter exists here).
  const movers = useGetMeteredUsageMoversQuery(
    { ...base, costCenterId, product, sku, groupBy: moverGroup, metric, limit: TOP_ROWS },
    { skip: !MOVERS_ENABLED || !ready || !caps.canViewMovers }
  );
  // Data coverage is only requested once the panel is opened, and never for a range past the report's limit.
  const qualityTooLong = rangeDays(range) > QUALITY_MAX_DAYS;
  const quality = useGetMeteredUsageQualityQuery(filterArgs, { skip: !DATA_COVERAGE_ENABLED || !ready || !caps.canViewQuality || !qualityOpen || qualityTooLong });

  // Options for the cost center filter (people, cost center and organization names now come from the API).
  const { data: costCenterList } = useGetCostCenterListQuery({ platformId, state: "active" }, { skip: !platformId });

  const productOptions = products.data?.data ?? [];
  // The "Net Cost after Deloitte Discount" tile covers everything in view: the billed (net) total less the discount.
  const deloitteTile = summary.data && (apiHasDeloitte || deloitteRate !== undefined) ? { rate: deloitteRate, percent: discountPercent, amount: summary.data.total } : undefined;

  const productColors = productColorMap(productOptions);

  // ── Expand windows (people: server paged; cost centers / organizations: paged here) ──
  const [openList, setOpenList] = useState(null); // "users" | "costCenters" | "organizations" | null
  const [listPage, setListPage] = useState(1);
  const [listSearch, setListSearch] = useState("");
  const [userSort, setUserSort] = useState({ by: "gross_amount", order: "desc" });
  const debouncedListSearch = useDebounce(listSearch, 300).trim();
  const openWindow = (name) => {
    setListPage(1);
    setListSearch("");
    setOpenList(name);
  };
  const closeWindow = () => setOpenList(null);

  const modalUsers = useGetMeteredUsageUsersQuery(
    { ...filterArgs, sortBy: userSort.by, sortOrder: userSort.order, page: listPage, perPage: PER_PAGE, search: debouncedListSearch || undefined },
    { skip: !ready || !caps.canViewUsers || openList !== "users" }
  );
  const toggleUserSort = (field) => {
    setUserSort((s) => (s.by === field ? { by: field, order: s.order === "desc" ? "asc" : "desc" } : { by: field, order: "desc" }));
    setListPage(1);
  };

  const groupSource = openList === "organizations" ? organizations.data?.data ?? [] : costCenters.data?.data ?? [];
  const groupNameKey = openList === "organizations" ? "org_name" : "cost_center_name";
  const filteredGroups = groupSource.filter((row) => !debouncedListSearch || (row[groupNameKey] ?? "").toLowerCase().includes(debouncedListSearch.toLowerCase()));
  const groupPages = Math.max(1, Math.ceil(filteredGroups.length / PER_PAGE));
  const groupRows = filteredGroups.slice((listPage - 1) * PER_PAGE, listPage * PER_PAGE);

  // Export: queues a CSV of the current scope and filters, then polls and downloads it through the shared export-job
  // flow (the same one the Copilot exports use). The server caps the range, so the button is off beyond it.
  const [startMeteredExport] = useStartMeteredUsageExportJobMutation();
  const exportScopeName = enterprise ? "Enterprise" : orgData?.name ?? "Organization";
  const { isExportingMetered, makeHandlers } = useCopilotExportJob(enterprise ? "metered-enterprise" : `metered-org-${orgId}`);
  const { handleMeteredExport } = makeHandlers({
    platformId,
    exportScopeName,
    queryFrom: range.from,
    queryTo: range.to,
    startMeteredJob: async () => {
      try {
        return await startMeteredExport(filterArgs).unwrap();
      } catch (err) {
        throw new Error(exportStartMessage(err));
      }
    },
  });
  const exportMaxDays = EXPORT_MAX_DAYS[scope];
  const exportTooLong = rangeDays(range) > exportMaxDays;
  const canRunExport = Boolean(caps.canCreateExport && caps.canReadExportJob && caps.canDownloadExportJob);
  const exportTooltip = !caps.canCreateExport
    ? "You don't have permission to export metered usage"
    : !caps.canReadExportJob || !caps.canDownloadExportJob
      ? "You can start exports but don't have permission to track or download them"
      : rangeProblem
        ? rangeProblem
        : exportTooLong
          ? `Exports cover at most ${exportMaxDays} days — choose a shorter period`
          : `Exports the billing usage for ${exportScopeName} as a CSV, for the current filters`;

  // First load of the page: nothing has arrived yet, so — like the Copilot dashboard — show one plain container with a
  // spinner instead of every card sitting empty. After that (a filter change, a refresh) the cards load in place.
  // The page-wide spinner only covers the moment before ANYTHING has come back. As soon as one section has answered (or failed)
  // the page shows, and each card that is still waiting shows its own skeleton, so a slow summary or people list never holds
  // the fast cards back.
  const anySectionAnswered = [summary, trend, products, topUsers, costCenters, organizations, lineTrend].some((query) => Boolean(query.data) || query.isError);
  const pageLoading = ready && caps.canViewSummary && !anySectionAnswered && (summary.isLoading || summary.isFetching);

  // One refresh (button + timed) for every section — see useMeteredUsageRefresh.
  const refreshAll = useMeteredUsageRefresh();
  const isRefreshing = [summary, trend, lineTrend, products, topUsers, costCenters, organizations, movers].some((q) => q.isFetching);

  const basePath = enterprise ? "/ecr-ops/metered-usage" : `/admin/${orgId}/metered-usage`;
  const rangeQuery = new URLSearchParams({ preset: "custom", from: range.from, to: range.to }).toString();
  const openUser = (row) => {
    closeWindow();
    // With an organization picked on the enterprise page the person opens on that organization's route (the data and
    // permissions are the organization's), but Back must return to the enterprise page with the same organization.
    const returnTo = pageIsEnterprise && orgFilter ? `/ecr-ops/metered-usage?${new URLSearchParams({ org: orgFilter, preset: "custom", from: range.from, to: range.to })}` : undefined;
    navigate(`${basePath}/users/${row.user_id}?${rangeQuery}`, { state: { user: row, returnTo } });
  };
  // On the enterprise page an organization row filters the page to that organization; on any other page it opens that
  // organization's own Metered Usage page.
  const openOrg = (row) => {
    closeWindow();
    if (pageIsEnterprise) update({ org: row.org_id });
    else navigate(`/admin/${row.org_id}/metered-usage?${rangeQuery}`);
  };
  const openCostCenter = (row) => {
    closeWindow();
    navigate(`/ecr-ops/cost-centers/${row.cost_center_id}`);
  };
  // What Changed is off for now (see MOVERS_ENABLED): these two belong to its rows.
  // // A mover row opens what it is: a product filters the page, a person / cost center / organization opens its page.
  // // Only real ids are followed — a fallback username is not a local person.
  // const canOpenMover = (mover, group) => {
  //   if (!mover.entity_id) return false;
  //   if (group === "product") return true;
  //   if (group === "user") return Boolean(caps.canViewUserDetail) && mover.identity_kind === "id";
  //   if (group === "cost_center") return enterprise && mover.identity_kind === "cost_center";
  //   if (group === "organization") return enterprise && mover.identity_kind === "organization";
  //   return false;
  // };
  // const openMover = (mover, group) => {
  //   if (group === "product") update({ product: mover.entity_id, sku: null });
  //   else if (group === "user") openUser({ user_id: mover.entity_id, full_name: mover.name, user_name: mover.user_name, email: mover.email });
  //   else if (group === "cost_center") openCostCenter({ cost_center_id: mover.entity_id });
  //   else if (group === "organization") openOrg({ org_id: mover.entity_id });
  // };

  // A picked organization the person has no Metered Usage permission for keeps the page (and its picker, so it can be
  // cleared) and says so, instead of replacing everything with an unavailable screen.
  const orgNoAccess = pageIsEnterprise && Boolean(orgFilter) && !caps.canViewSummary;
  if (!caps.canViewSummary && !orgNoAccess) {
    return (
      <UnauthorizedAccess
        message={`You do not have permission to view ${pageIsEnterprise ? "enterprise " : ""}metered usage.`}
        primaryLabel="Back"
        primaryTo={pageIsEnterprise ? "/ecr-ops" : `/admin/${orgId}/dashboard`}
      />
    );
  }

  const selectedCostCenter = (costCenterList ?? []).find((cc) => cc._id === costCenterId);
  const topRows = topUsers.data?.data ?? [];
  const modalRows = modalUsers.data?.data ?? [];
  const modalTotal = modalUsers.data?.total ?? 0;
  const usersTotals = topUsers.data?.totals;
  const ccRows = costCenters.data?.data ?? [];
  const orgRows = organizations.data?.data ?? [];

  const sortButton = (field, label) => (
    <Tooltip key={field} title={`Sort by ${label}${userSort.by === field ? (userSort.order === "asc" ? " (low → high)" : " (high → low)") : ""}`} placement="top">
      <span>
      <IconButton
        size="small"
        aria-label={`Sort by ${label}`}
        disabled={modalUsers.isFetching}
        onClick={() => toggleUserSort(field)}
        sx={{
          border: userSort.by === field ? `2px solid ${ACCENT}` : "1px solid #e8e8e8",
          borderRadius: "6px",
          backgroundColor: userSort.by === field ? "#f2f8e6" : "#fff",
          color: userSort.by === field ? ACCENT : "#555",
          minWidth: 34,
        }}
      >
        {userSort.by === field ? userSort.order === "desc" ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" /> : <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>{label[0]}</Typography>}
      </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        flex: "1",
        marginTop: pageIsEnterprise ? 0 : { xs: "1.5rem", md: "4rem" },
        marginBottom: pageIsEnterprise ? 0 : { xs: "1.5rem", md: "4rem" },
        marginLeft: pageIsEnterprise ? 0 : { xs: "1rem", md: "4rem" },
        marginRight: pageIsEnterprise ? 0 : { xs: "1rem", md: "4rem" },
        backgroundColor: "#F3F3F3",
        borderRadius: "12px",
        padding: { xs: "16px", md: "24px" },
        minWidth: 0,
      }}
    >
      {/* Page header — same layout as the Copilot dashboard */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, alignItems: { xs: "flex-start", lg: "center" }, justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ReceiptLongIcon sx={{ fontSize: 30, color: "#1a1a1a" }} />
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#43B02A", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
              {enterprise ? "Enterprise" : orgData?.name ?? "Organization"}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
              Metered Usage
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#888", mt: 0.3 }}>
              Billing usage &amp; spend across every GitHub product{enterprise ? " and organization" : ""}
            </Typography>
            <HeaderDatePill from={range.from} to={range.to} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", lg: "flex-end" }, gap: 1, width: { xs: "100%", lg: "auto" } }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <RefreshButton label="Refresh metered usage" isRefreshing={isRefreshing} onClick={refreshAll} size={30} />
            {caps.canViewExport && (
              <Tooltip title={exportTooltip} placement="top" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={isExportingMetered ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
                    onClick={handleMeteredExport}
                    disabled={isExportingMetered || !ready || !canRunExport || exportTooLong}
                    sx={{
                      borderRadius: "16px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      borderColor: "#ccc",
                      color: "#555",
                      px: 1.5,
                      py: 0.3,
                      minWidth: 0,
                      "&:hover": { borderColor: ACTION_GREEN, color: ACTION_GREEN },
                      "&.Mui-disabled": { borderRadius: "16px", borderColor: "#e0e0e0", color: "#bbb" },
                    }}
                  >
                    {isExportingMetered ? "Exporting..." : "Export Usage"}
                  </Button>
                </span>
              </Tooltip>
            )}
            {pageIsEnterprise && pageCaps.canViewExport && (
              <Tooltip title="See and download the reports you exported before" placement="top" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate("/ecr-ops/metered-usage/exports")}
                    sx={{ borderRadius: "16px", textTransform: "none", fontSize: "0.8rem", fontWeight: 500, borderColor: "#ccc", color: "#555", px: 1.5, py: 0.3, minWidth: 0, "&:hover": { borderColor: ACTION_GREEN, color: ACTION_GREEN } }}
                  >
                    Export History
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {PRESETS.map((preset) => (
              <Button key={preset.key} size="small" onClick={(event) => choosePreset(preset.key, event)} sx={pillSx(activePreset === preset.key)}>
                {preset.label}
              </Button>
            ))}
          </Box>
          <RangeCalendar open={Boolean(calendarAnchor)} anchorEl={calendarAnchor} from={range.from} to={range.to} onApply={applyCustomRange} onClose={closeCalendar} />
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
        <ProductSkuFilter products={productOptions} product={product} sku={sku} metric={metric} onChange={({ product: nextProduct, sku: nextSku }) => update({ product: nextProduct, sku: nextSku })} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", minWidth: 56 }}>Filter</Typography>
          {pageIsEnterprise && (
            // Searchable, A–Z. Picking one switches the page to that organization's data; clearing goes back to the whole enterprise.
            <Autocomplete
              size="small"
              options={orgChoices}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={orgChoices.find((org) => org._id === orgFilter) ?? (orgFilter ? { _id: orgFilter, name: orgData?.name ?? "Loading…" } : null)}
              onChange={(_, option) => update({ org: option?._id ?? null })}
              noOptionsText="No matching organization"
              componentsProps={{ popper: { style: { width: "fit-content", minWidth: 280 } } }}
              sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: "16px", backgroundColor: "#fff", fontSize: "0.8rem", py: 0 } }}
              renderInput={(params) => <TextField {...params} placeholder="All organizations" inputProps={{ ...params.inputProps, "aria-label": "Organization" }} />}
            />
          )}
          <Autocomplete
            size="small"
            options={sortByLabel(costCenterList, "name")}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={(costCenterList ?? []).find((cc) => cc._id === costCenterId) ?? null}
            onChange={(_, option) => update({ cc: option?._id ?? null })}
            sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: "16px", backgroundColor: "#fff", fontSize: "0.8rem", py: 0 } }}
            renderInput={(params) => <TextField {...params} placeholder="All cost centers" inputProps={{ ...params.inputProps, "aria-label": "Cost center" }} />}
          />
          <Box sx={{ display: "flex", gap: 0.5 }} role="group" aria-label="Measure">
            {metrics.map((m) => (
              <Button key={m.key} size="small" onClick={() => update({ metric: m.key === "net_amount" ? null : m.key })} sx={pillSx(metric === m.key)}>
                {m.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {pageLoading && (
        <>
          <LinearProgress sx={{ mb: 2, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: ACTION_GREEN } }} />
          <Box
            role="status"
            aria-label="Loading"
            data-testid="page-loading"
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px", backgroundColor: "#fafafa", borderRadius: "12px", border: "1px solid #e8e8e8" }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <CircularProgress sx={{ color: ACTION_BLUE }} />
              <Typography sx={{ fontSize: "0.9rem", color: "#888" }}>Fetching data...</Typography>
            </Box>
          </Box>
        </>
      )}

      {orgNoAccess && (
        <Box data-testid="org-no-access" sx={{ p: 4, textAlign: "center", backgroundColor: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px" }}>
          <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>
            You don't have permission to view Metered Usage for {orgData?.name ?? "this organization"}.
          </Typography>
          <Button size="small" onClick={() => update({ org: null })} sx={{ mt: 1, textTransform: "none", color: ACCENT }}>
            Back to all organizations
          </Button>
        </Box>
      )}

      {!pageLoading && !orgNoAccess && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* What the totals below describe */}
        <Typography data-testid="scope-line" sx={{ fontSize: "0.78rem", color: "#777", mt: -1.5 }}>
          Showing <b>{metricLabel(metric)}</b> · <b>{product ? `${productLabel(product)}${sku ? ` › ${skuLabel(sku, product)}` : ""}` : "All products"}</b> · {pageIsEnterprise && (
            <>
              <b>{orgFilter ? orgData?.name ?? "Organization" : "All organizations"}</b> ·{" "}
            </>
          )}
          <b>{selectedCostCenter?.name ?? "All cost centers"}</b> · {range.from} → {range.to} (UTC)
        </Typography>
        {rangeProblem && (
          <Typography role="alert" sx={{ color: "#E53935", fontSize: "0.85rem" }}>
            {rangeProblem} Nothing is loaded until the dates are fixed.
          </Typography>
        )}

        {/* KPIs */}
        <KpiCards range={range} deloitte={deloitteTile} summary={summary.data} isLoading={summary.isLoading || summary.isFetching} />
        {summary.isError && (
          <Typography sx={{ color: "#E53935", fontSize: "0.85rem" }}>
            Couldn't load the totals for this period.{" "}
            <Button size="small" onClick={summary.refetch} sx={{ textTransform: "none", color: ACCENT }}>
              Retry
            </Button>
          </Typography>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: caps.canViewTrend && caps.canViewProducts ? "minmax(0, 2fr) minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 3, alignItems: "stretch" }}>
        {caps.canViewTrend && (
          <Card
            title="Cost Trend"
            subtitle={`${metricLabel(metric)} cost by ${granularity === "month" ? "month" : "day"} and product · days with no usage have no bar`}
            from={range.from}
            to={range.to}
            headerMinHeight={HEADER_MIN_HEIGHT}
            loading={trend.isFetching}
            isInitialLoading={!trend.data && trend.isFetching}
            skeleton="chart"
            actions={
              <Box sx={{ display: "flex", gap: 0.5 }} role="group" aria-label="Granularity">
                {[
                  ["day", "Daily"],
                  ["month", "Monthly"],
                ].map(([key, label]) => (
                  <Button key={key} size="small" onClick={() => update({ gran: key === "day" ? null : key })} sx={pillSx(granularity === key)}>
                    {label}
                  </Button>
                ))}
              </Box>
            }
          >
            {trend.isError ? (
              <LoadError onRetry={trend.refetch}>Couldn't load the trend.</LoadError>
            ) : (
              // The chart follows what the response says it is grouped by, so it never mixes an old shape with a new request.
              <TrendChart points={trend.data?.data} granularity={granularity} metric={metric} groupBy={trend.data?.group_by ?? undefined} colors={productColors} />
            )}
          </Card>
        )}

          {/* Where the cost comes from: ranked, labelled bars (a donut is one click away) */}
          {caps.canViewProducts && (
            <Card
              title="Spend by Product"
              subtitle={`${metricLabel(metric)} by product · all products in this scope${product ? " — not narrowed by the product filter" : ""}`}
              from={range.from}
              to={range.to}
              hideDate
              headerMinHeight={HEADER_MIN_HEIGHT}
              loading={products.isFetching}
              isInitialLoading={!products.data && products.isFetching}
              skeleton="list"
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, pt: 1.5 }}>
                <Box sx={{ display: "flex", gap: 0.5 }} role="group" aria-label="Product view">
                  {[
                    ["bars", "Bars"],
                    ["donut", "Donut"],
                  ].map(([key, label]) => (
                    <Button key={key} size="small" aria-pressed={productView === key} onClick={() => setProductView(key)} sx={pillSx(productView === key)}>
                      {label}
                    </Button>
                  ))}
                </Box>
              </Box>
              {products.isError ? (
                <LoadError onRetry={products.refetch}>Couldn't load the products.</LoadError>
              ) : productView === "donut" ? (
                <ProductShareDonut products={products.data?.data} metric={metric} selectedProduct={product} onSelect={(id) => update({ product: id, sku: null })} />
              ) : (
                <ProductBars products={products.data?.data} metric={metric} selectedProduct={product} onSelect={(id) => update({ product: id, sku: null })} />
              )}
            </Card>
          )}
        </Box>

        {/* What changed since the previous period — switched off for now (see MOVERS_ENABLED). To restore, remove this comment.
        {caps.canViewMovers && (
          <WhatChanged
            data={movers.data}
            isLoading={!movers.data && movers.isFetching}
            isFetching={movers.isFetching}
            isError={movers.isError}
            onRetry={movers.refetch}
            groups={moverGroups}
            groupBy={moverGroup}
            onGroupBy={(key) => update({ mvg: key === "product" ? null : key })}
            tab={moverTab}
            onTab={setMoverTab}
            metric={metric}
            canOpen={canOpenMover}
            onOpen={openMover}
          />
        )}
        */}

        {/* Products & SKUs */}
        {caps.canViewProducts && (
          <Card
            title="Products & SKUs"
            subtitle={`Click a product to see its SKUs. Quantities are per SKU, shown with their unit, and never added across units.${product ? " Lists every product — not narrowed by the product filter." : ""}`}
            from={range.from}
            to={range.to}
            loading={products.isFetching}
            isInitialLoading={!products.data && products.isFetching}
            skeleton="table"
          >
            {products.isError ? <LoadError onRetry={products.refetch}>Couldn't load the products.</LoadError> : <ProductsTable products={products.data?.data} deloitteRate={deloitteOverageRate} discountPercent={discountPercent} range={range} selectedProduct={product} selectedSku={sku} onFilterProduct={(id) => update({ product: id === product ? null : id, sku: null })} />}
          </Card>
        )}

        {/* Top contributors */}
        {caps.canViewUsers && (
          <Card
            title="Top Cost Contributors"
            subtitle={`Showing top ${Math.min(TOP_ROWS, topUsers.data?.total ?? TOP_ROWS)} of ${(topUsers.data?.total ?? 0).toLocaleString("en-US")} people — sorted by highest gross usage${caps.canViewUserDetail ? " · click a person for their whole-period breakdown" : ""}`}
            from={range.from}
            to={range.to}
            loading={topUsers.isFetching}
            isInitialLoading={!topUsers.data && topUsers.isFetching}
            skeleton="table"
            actions={<ExpandButton label="View everyone with full data" onClick={() => openWindow("users")} />}
          >
            {topUsers.isError ? (
              <LoadError onRetry={topUsers.refetch}>Couldn't load the people for this period.</LoadError>
            ) : topRows.length === 0 ? (
              <EmptyNote>No one has usage in this period.</EmptyNote>
            ) : (
              <UsersTable
                deloitteRate={deloitteRate}
            discountPercent={discountPercent}
                rows={topRows}
                startIndex={0}
                enterprise={enterprise}
                metric={metric}
                totals={usersTotals}
                totalCount={topUsers.data?.total}
                canOpen={caps.canViewUserDetail}
                onOpen={openUser}
              />
            )}
          </Card>
        )}

        {/* Cost centers */}
        {caps.canViewCostCenters && (
          <Card
            title="By Cost Center"
            subtitle={`Showing top ${Math.min(TOP_ROWS, ccRows.length)} of ${ccRows.length.toLocaleString("en-US")} cost centers — sorted by highest net cost${costCenterId ? " · lists every cost center, not just the one filtered" : ""}`}
            from={range.from}
            to={range.to}
            loading={costCenters.isFetching}
            isInitialLoading={!costCenters.data && costCenters.isFetching}
            skeleton="table"
            actions={<ExpandButton label="View all cost centers" onClick={() => openWindow("costCenters")} />}
          >
            {costCenters.isError ? (
              <LoadError onRetry={costCenters.refetch}>Couldn't load the cost centers.</LoadError>
            ) : ccRows.length === 0 ? (
              <EmptyNote>No cost center spend in this period.</EmptyNote>
            ) : (
              <GroupTable deloitteRate={deloitteRate} discountPercent={discountPercent} rows={ccRows.slice(0, TOP_ROWS)} nameKey="cost_center_name" idKey="cost_center_id" nameHeader="Cost center" metric={metric} totals={costCenters.data?.totals} onOpen={enterprise ? openCostCenter : undefined} testId="cost-center-row" />
            )}
          </Card>
        )}

        {/* Organizations (enterprise only) */}
        {enterprise && caps.canViewOrganizations && (
          <Card
            title="By Organization"
            subtitle={`Showing top ${Math.min(TOP_ROWS, orgRows.length)} of ${orgRows.length.toLocaleString("en-US")} organizations — open an organization to see its own usage`}
            from={range.from}
            to={range.to}
            loading={organizations.isFetching}
            isInitialLoading={!organizations.data && organizations.isFetching}
            skeleton="table"
            actions={<ExpandButton label="View all organizations" onClick={() => openWindow("organizations")} />}
          >
            {organizations.isError ? (
              <LoadError onRetry={organizations.refetch}>Couldn't load the organizations.</LoadError>
            ) : orgRows.length === 0 ? (
              <EmptyNote>No organization spend in this period.</EmptyNote>
            ) : (
              <GroupTable deloitteRate={deloitteRate} discountPercent={discountPercent} rows={orgRows.slice(0, TOP_ROWS)} nameKey="org_name" idKey="org_id" nameHeader="Organization" metric={metric} totals={organizations.data?.totals} onOpen={openOrg} testId="org-row" />
            )}
          </Card>
        )}

        {/* Cost over time, as lines */}
        {caps.canViewTrend && (
          <Card
            title="Cost Over Time"
            subtitle={`${metricLabel(metric)} cost per ${lineGranularity === "month" ? "month" : "day"}${lineGroup ? ` for each ${lineGroup === "sku" ? "SKU" : "product"}` : " in total"}${cumulative ? ", as a running total" : ""} · same period and filters as above`}
            from={range.from}
            to={range.to}
            loading={lineTrend.isFetching}
            isInitialLoading={!lineTrend.data && lineTrend.isFetching}
            skeleton="chart"
            actions={
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", gap: 0.5 }} role="group" aria-label="Lines">
                  {[
                    ["total", "Total", true],
                    ["product", "By product", true],
                    ["sku", "By SKU", Boolean(product)],
                  ].map(([key, label, enabled]) => (
                    <Tooltip key={key} title={enabled ? "" : "Pick a product first to see its SKUs"} arrow>
                      <span>
                        <Button size="small" disabled={!enabled} aria-pressed={(lineGroup || "total") === key} onClick={() => update({ lines: key === "product" ? null : key })} sx={pillSx((lineGroup || "total") === key)}>
                          {label}
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                </Box>
                <Button size="small" aria-pressed={cumulative} onClick={() => update({ cum: cumulative ? null : "1" })} sx={pillSx(cumulative)}>
                  Cumulative
                </Button>
                <Box sx={{ display: "flex", gap: 0.5 }} role="group" aria-label="Line interval">
                  {[
                    ["day", "Daily"],
                    ["month", "Monthly"],
                  ].map(([key, label]) => (
                    <Button key={key} size="small" aria-pressed={lineGranularity === key} onClick={() => update({ lgran: key === "day" ? null : key })} sx={pillSx(lineGranularity === key)}>
                      {label}
                    </Button>
                  ))}
                </Box>
              </Box>
            }
          >
            {lineTrend.isError ? (
              <LoadError onRetry={lineTrend.refetch}>Couldn't load the cost over time.</LoadError>
            ) : (
              <TrendLines points={lineTrend.data?.data} granularity={lineGranularity} metric={metric} groupBy={lineTrend.data?.group_by ?? undefined} colors={productColors} cumulative={cumulative} />
            )}
          </Card>
        )}

        {/* Data coverage — collapsed; asks the API only once opened */}
        {DATA_COVERAGE_ENABLED && caps.canViewQuality && (
          <QualityPanel
            expanded={qualityOpen}
            onToggle={() => setQualityOpen((open) => !open)}
            data={quality.data}
            isLoading={!quality.data && quality.isFetching}
            isError={quality.isError}
            onRetry={quality.refetch}
            range={range}
            metric={metric}
            rangeTooLong={qualityTooLong}
            maxDays={QUALITY_MAX_DAYS}
          />
        )}
      </Box>
      )}

      {/* ── Expand windows ── */}
      <FullListDialog
        open={openList === "users"}
        onClose={closeWindow}
        title="Everyone — billing usage"
        subtitle={`${modalTotal.toLocaleString("en-US")} ${modalTotal === 1 ? "person" : "people"}${debouncedListSearch ? ` · matching "${debouncedListSearch}"` : ""} · ${range.from} → ${range.to}`}
        search={listSearch}
        onSearchChange={(value) => {
          setListSearch(value);
          setListPage(1);
        }}
        searchPlaceholder="Search name or email"
        controls={
          <>
            {sortButton("gross_amount", "Gross")}
            {sortButton("net_amount", "Net")}
            {sortButton("discount_amount", "Discount")}
            {apiHasDeloitte && sortButton("deloitte_net_amount", "Deloitte net")}
            {apiHasDeloitte && sortButton("deloitte_discount_amount", "Deloitte discount")}
          </>
        }
        loading={modalUsers.isFetching}
        page={listPage}
        totalPages={Math.max(1, Math.ceil(modalTotal / PER_PAGE))}
        totalCount={modalTotal}
        perPage={PER_PAGE}
        noun="people"
        onPageChange={setListPage}
      >
        {modalRows.length === 0 && !modalUsers.isFetching ? (
          <EmptyNote>No one matches.</EmptyNote>
        ) : (
          <UsersTable
            deloitteRate={deloitteRate}
            discountPercent={discountPercent}
            rows={modalRows}
            startIndex={(listPage - 1) * PER_PAGE}
            enterprise={enterprise}
            metric={metric}
            totals={modalUsers.data?.totals}
            totalCount={modalTotal}
            canOpen={caps.canViewUserDetail}
            onOpen={openUser}
          />
        )}
      </FullListDialog>

      <FullListDialog
        open={openList === "costCenters" || openList === "organizations"}
        onClose={closeWindow}
        title={openList === "organizations" ? "All organizations — billing usage" : "All cost centers — billing usage"}
        subtitle={`${filteredGroups.length.toLocaleString("en-US")} ${openList === "organizations" ? "organizations" : "cost centers"}${debouncedListSearch ? ` · matching "${debouncedListSearch}"` : ""} · ${range.from} → ${range.to}`}
        search={listSearch}
        onSearchChange={(value) => {
          setListSearch(value);
          setListPage(1);
        }}
        searchPlaceholder="Search by name"
        page={listPage}
        totalPages={groupPages}
        totalCount={filteredGroups.length}
        perPage={PER_PAGE}
        noun={openList === "organizations" ? "organizations" : "cost centers"}
        onPageChange={setListPage}
      >
        {groupRows.length === 0 ? (
          <EmptyNote>Nothing matches.</EmptyNote>
        ) : openList === "organizations" ? (
          <GroupTable deloitteRate={deloitteRate} discountPercent={discountPercent} rows={groupRows} nameKey="org_name" idKey="org_id" nameHeader="Organization" metric={metric} totals={organizations.data?.totals} onOpen={openOrg} testId="org-row" />
        ) : (
          <GroupTable deloitteRate={deloitteRate} discountPercent={discountPercent} rows={groupRows} nameKey="cost_center_name" idKey="cost_center_id" nameHeader="Cost center" metric={metric} totals={costCenters.data?.totals} onOpen={enterprise ? openCostCenter : undefined} testId="cost-center-row" />
        )}
      </FullListDialog>
    </Box>
  );
};

export default MeteredUsageDashboard;
